/**
 * The first-paint script, as text (spec docs/superpowers/specs/2026-09-27-app-ready-made-themes-design.md,
 * "Ruling 3").
 *
 * The app sidebar renders it first (components/app-theme/app-theme-scope.tsx), during server
 * rendering, so it runs while the page is being parsed — before any app content paints. It reads the
 * hf-app-theme cookie and, for a choice the person made (Classic and Custom included) on an app page,
 * puts `data-app-theme` and the Inter variable class on <html>. No cookie — never chosen — paints
 * nothing: the page exactly as it was before themes existed. It follows the rules of app-theme-cookie.ts and app-theme-dom.ts (a spec
 * runs them all over the same cases); it is written out by hand, in plain ES5, because a function's
 * own source (`fn.toString()`) would be whatever the bundler turned it into.
 *
 * SAFETY. The script is static: the same text whatever the cookie says. It carries the catalogue as
 * data and compares the cookie against it, so only a catalogue choice can ever reach <html>; the font
 * class is embedded only when it is a plain class name; every value is JSON with `<`, `>`, `&` and
 * the line separators escaped.
 */
import { APP_THEME_ATTRIBUTE, APP_THEME_CHOICES, APP_THEME_COOKIE } from './app-themes'
import { APP_THEME_OFF_PATHS, isPlainClassName } from './app-theme-dom'

/** JSON that is also safe inside a <script> element. */
function literal(value: unknown): string {
  return JSON.stringify(value)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029')
}

const SCRIPT = [
  'try{',
  'var p=location.pathname,i,s,m,v,t,d;',
  // The play area, the portal and the console: never, whatever the cookie says.
  'for(i=0;i!==a.off.length;i++){s=a.off[i];if(p===s||p.indexOf(s+"/")===0)return;}',
  String.raw`m=new RegExp("(?:^|;\\s*)"+a.name+"=([^;]*)").exec(document.cookie);`,
  'if(!m)return;',
  // <theme>[.<owner>] — the same shape parseAppThemeCookieValue accepts, nothing else.
  'v=m[1].split(".");',
  'if(v.length>2)return;',
  't=v[0];',
  'for(i=0;i!==a.themes.length;i++)if(a.themes[i]===t)break;',
  'if(i===a.themes.length)return;',
  String.raw`if(v.length===2&&!/^[A-Za-z0-9-]{1,64}$/.test(v[1]))return;`,
  'd=document.documentElement;',
  'd.setAttribute(a.attr,t);',
  'if(a.font)d.classList.add(a.font);',
  '}catch(e){}',
].join('')

/** The script's source. `fontClass` is dropped unless it is a plain class name. */
export function appThemeBootScript(fontClass?: string): string {
  const args = {
    name: APP_THEME_COOKIE,
    attr: APP_THEME_ATTRIBUTE,
    themes: [...APP_THEME_CHOICES],
    off: [...APP_THEME_OFF_PATHS],
    font: isPlainClassName(fontClass) ? fontClass : null,
  }
  return `(function(a){${SCRIPT}})(${literal(args)})`
}
